import { atom } from 'jotai';
import { isFunction } from 'remeda';

import { MeasureSchemaType, SeedlingGetOutstandingMeasuresQuery } from '@grow-therapy-team/seedling-components';

import { FormDataGroup, FormValuesByFormTitle } from '../components/forms/types';
import { combineFormDataV2 } from '../components/forms/utils';
import { chatParticipantsByIdAtom } from '../session/state';
import { AppState, UserType } from '../types';
import { createDerivedWritableAtom, shortUuid } from '../utils';
import { parseIdentity } from '../video-sdk';

export enum VisitorState {
  INTRO = AppState.INTRO,
  PRESESSION_MEASURES = AppState.PRESESSION_MEASURES,
  WAITING = AppState.WAITING,
  /**
   * Basically the same as the loading state, but for manual join. It was easier
   * to add a new state than to rewire the other loading state. We'll eventually
   * remove the loading state.
   */
  JOINING = 'joining',
  LOADING = 'loading',
  MEETING = AppState.MEETING,
  COMPLETED = 'completed',
  TIMEOUT = AppState.TIMEOUT,
  TAB_AWAY = AppState.TAB_AWAY,
}

export enum VisitorDrawerState {
  CHAT = 'CHAT',
  INTENTIONS = 'INTENTIONS',
  PENDING_FORMS = 'PENDING_FORMS',
  PENDING_FORMS_COMPLETED = 'PENDING_FORMS_COMPLETED',
  REFLECTION_TOPICS = 'REFLECTION_TOPICS',
  SETTINGS = 'SETTINGS',
}

export enum VisitorModalState {
  CONSENT = 'CONSENT',
  HELP = 'HELP',
  PROVIDER_NOT_HERE = 'PROVIDER_NOT_HERE',
  ADMISSION = 'ADMISSION',
}

const MEETING_STATES = new Set([VisitorState.LOADING, VisitorState.MEETING, VisitorState.COMPLETED]);

export type OutstandingMeasures = SeedlingGetOutstandingMeasuresQuery['outstandingMeasures'];

export type VisitorFormData = FormDataGroup;

export type VisitorAtom = {
  name: string;
  state: VisitorState;
  visitorUuid: string;
  visitorDrawerState: VisitorDrawerState | null;
  activeConversationSid?: string;
  sessionConversationSid?: string;
  /**
   * Short ID for the appointment that the visitor joined the meeting with
   */
  sessionAppointmentShortId?: string;
  customerId: string;
  // This is the provider's monolith PK ID, it's used for certain monolith mutations
  providerId: string;
  providerIsOnline: boolean | null;
  formData: VisitorFormData;
  isHelpModalOpen: boolean;
  visitorAccessDocSid?: string;
  measures?: OutstandingMeasures;
  preSessionVibesOpen: boolean;
  isProviderNotHereModalOpen: boolean;
  /**
   * Tracks whether the user has interacted with the Vertical Vibes keyboard
   * If they interact with it, navigate away, then navigate back
   * we can skip the instructions when they return.
   */
  preSessionVibesTutorialCompleted: boolean;
  /**
   * Provided during admission for limited API access
   */
  apiToken?: string;
  hasCompletedClientMeasures: boolean;
  /**
   * Track if the provider initiated restarting the session, to change how we
   * react to room/participant disconnects (e.g. to skip the post-session
   * redirect)
   */
  isRestartingSession: boolean;
  /**
   * Number of times the visitor has been admitted
   */
  admissionAttempts: number;
  /**
   * Number of times the visitor has attempted to join the room
   */
  joinAttempts: number;
  isAdmissionModalOpen: boolean;
  /**
   * ISO date time string when the visitor was admitted to the meeting room.
   * This is generated on the server side when the provider admits the visitor.
   */
  admissionTime?: string;
};

export type CreateVisitorOptions = {
  state?: VisitorState;
  name?: string;
};

export function createVisitor({ state = VisitorState.INTRO, name = '' }: CreateVisitorOptions): VisitorAtom {
  return {
    name,
    state,
    customerId: '',
    visitorUuid: shortUuid(),
    providerId: '',
    visitorDrawerState: null,
    providerIsOnline: null,
    isHelpModalOpen: false,
    formData: { providerForms: [], formResponses: [] },
    measures: undefined,
    preSessionVibesTutorialCompleted: false,
    preSessionVibesOpen: false,
    hasCompletedClientMeasures: false,
    isRestartingSession: false,
    isAdmissionModalOpen: false,
    admissionAttempts: 0,
    joinAttempts: 0,
    isProviderNotHereModalOpen: false,
  };
}

export const visitorAtom = atom<VisitorAtom>(createVisitor({ name: '', state: VisitorState.INTRO }));

export const apiTokenAtom = createDerivedWritableAtom(visitorAtom, 'apiToken');

export const visitorNameAtom = createDerivedWritableAtom(visitorAtom, 'name');

export const visitorStateAtom = createDerivedWritableAtom(visitorAtom, 'state');

export const visitorDrawerStateAtom = createDerivedWritableAtom(visitorAtom, 'visitorDrawerState');

/**
 * Tracks whether the current PENDING_FORMS measures drawer session was opened
 * automatically by `useAutoOpenPreSessionDrawer` (vs. manually via the
 * control-panel button). Used by `useAutoCloseMeasuresDrawer` to decide
 * whether to transition to REFLECTION_TOPICS after measures complete — only
 * the auto-open path should trigger that transition. Reset to `false` when
 * the drawer leaves PENDING_FORMS.
 */
export const wasMeasuresDrawerAutoOpenedAtom = atom<boolean>(false);

/**
 * Tracks whether the client was shown the consent hard-block at any point
 * during the current session. When the hard-block is displayed, flip this to
 * `true` so the value can later be appended to the soft-block eligibility
 * check (see `resolve_soft_block_eligibility` on the server, where consent
 * hard-block checks are layered on by the frontend). Defaults to `false`.
 */
export const experiencedHardBlockThisSessionAtom = atom<boolean>(false);

export const isSoftBlockResolvedAtom = atom<boolean>(false);

export const didManuallyResolveSoftBlockAtom = atom<boolean>(false);

/**
 * The page the client is currently on within the soft-block flow, used so
 * flow-spanning events (the guardrail prompt + auto-transition, and the skip
 * action) can report "which page" the client was on. `PageWrapper` seeds it to
 * `SOFT_BLOCK_GET_STARTED_STEP`; `PreSessionMeasures` updates it to the current
 * measure type as the client pages through measures.
 */
export const SOFT_BLOCK_GET_STARTED_STEP = 'get_started';

export type SoftBlockStep = typeof SOFT_BLOCK_GET_STARTED_STEP | MeasureSchemaType;

export const softBlockCurrentStepAtom = atom<SoftBlockStep>(SOFT_BLOCK_GET_STARTED_STEP);

/**
 * Guards the `SOFT_BLOCK_ELIGIBILITY_EVALUATED` event so it fires exactly once
 * per session — on the first (determining) routing evaluation — rather than on
 * every subsequent `enterWaitingRoom` call (e.g. when the soft block is later
 * resolved via skip / completion / auto-transition).
 */
export const hasEvaluatedSoftBlockEligibilityAtom = atom<boolean>(false);

/**
 * Whether the client legally requires telehealth safety guards — true for
 * clients under 13 (see `clientRequiresTelehealthSafetyGuards`). Resolved from
 * the monolith during the pre-render gather phase by
 * `useResolveClientSafetyGuardCallback`, before the routing decision, so the
 * soft-block eligibility check can exclude under-13 clients (COPPA). `undefined`
 * until resolved; the routing decision treats unknown as "requires guards" to
 * err on the side of never soft-blocking a minor.
 */
export const clientRequiresSafetyGuardsAtom = atom<boolean | undefined>(undefined);

/**
 * Whether the client is excluded from the telehealth soft block based on their
 * payor — today, KPNC (Kaiser Permanente Nor Cal) patients, gated behind a
 * feature flag so the exclusion can be toggled without a deploy (see
 * `clientExcludedFromSoftBlockByPayor`). Resolved from the monolith during the
 * pre-render gather phase by `useResolvePayorSoftBlockExclusionCallback`, before
 * the routing decision. `undefined` until resolved; the routing decision treats
 * unknown as "excluded" to fail safe (never soft-block an excluded payor).
 */
export const isPayorExcludedFromSoftBlockAtom = atom<boolean | undefined>(undefined);

export const visitorIsHelpModalOpenAtom = createDerivedWritableAtom(visitorAtom, 'isHelpModalOpen');

/**
 * Short ID for the appointment that the visitor joined the meeting with
 *
 * Should be set when the visitor joins a meeting and unset when the visitor
 * leaves
 */
export const sessionAppointmentShortIdAtom = createDerivedWritableAtom(visitorAtom, 'sessionAppointmentShortId');

export const visitorGroupedFormsAtom = atom((get) => {
  const { providerForms, formResponses } = get(visitorFormData) ?? {};

  if (!providerForms) return;
  return Object.values(combineFormDataV2(formResponses, providerForms)).flat();
});

export const visitorFormValues = atom(
  (get) => get(visitorFormData)?.formValuesByTitle,
  (_get, set, valueOrFunction: FormValuesByFormTitle | ((prev?: FormValuesByFormTitle) => FormValuesByFormTitle)) => {
    set(visitorFormData, (prev) => {
      const prevFormValuesByTitle = prev?.formValuesByTitle;
      return {
        ...prev,
        formValuesByTitle: isFunction(valueOrFunction) ? valueOrFunction(prevFormValuesByTitle) : valueOrFunction,
      };
    });
  },
);

export const visitorFormData = atom(
  (get) => get(visitorAtom).formData,
  (_get, set, valueOrFunction: FormDataGroup | ((prev: FormDataGroup) => FormDataGroup)) => {
    set(visitorAtom, (prev) => ({
      ...prev,
      formData: isFunction(valueOrFunction) ? valueOrFunction(prev?.formData) : valueOrFunction,
    }));
  },
);

export const visitorMeasuresAtom = atom(
  (get) => get(visitorAtom).measures,
  (_get, set, valueOrFunction: OutstandingMeasures | ((prev: OutstandingMeasures) => OutstandingMeasures)) => {
    set(visitorAtom, (prev) => ({
      ...prev,
      measures: isFunction(valueOrFunction) ? valueOrFunction(prev?.measures) : valueOrFunction,
    }));
  },
);

/**
 * The current page index of the client measures drawer, used to preserve the
 * page when the visitor transitions from the waiting room to the meeting room
 * and the drawer remounts.
 */
export const visitorMeasuresPageIndexAtom = atom(0);

/**
 * The soft-block-eligible measure types (a subset of GAD-7 / PHQ-9 / PHQ-9A)
 * the client hasn't completed within the staleness window, resolved from the
 * monolith by `useResolveStaleSoftBlockMeasuresCallback` during the pre-render
 * gather phase. `undefined` until resolved (or skipped for ineligible clients).
 * Read during routing to gate soft-block entry on at least one stale measure
 * existing, and read when rendering the soft block to filter the visitor's
 * outstanding measures down to just these types.
 */
export const staleSoftBlockMeasureTypesAtom = atom<MeasureSchemaType[] | undefined>(undefined);

/**
 * When using external APIs, do _not_ use this atom as a reference to the
 * visitor alone, use useVisitorId hook instead
 */
export const visitorUuidAtom = atom((get) => get(visitorAtom).visitorUuid);

export const activeConversationSidAtom = createDerivedWritableAtom(visitorAtom, 'activeConversationSid');

export const shouldShowStaleVersionToastAtom = atom((get) => !MEETING_STATES.has(get(visitorAtom).state));

export const providerParticipantAtom = atom((get) =>
  Object.values(get(chatParticipantsByIdAtom)).find(
    (participant) => parseIdentity(participant.identity).userType === UserType.PROVIDER,
  ),
);

export const providerIsOnlineAtom = createDerivedWritableAtom(visitorAtom, 'providerIsOnline');

export const visitorAccessDocSidAtom = createDerivedWritableAtom(visitorAtom, 'visitorAccessDocSid');

export const preSessionVibesOpenAtom = createDerivedWritableAtom(visitorAtom, 'preSessionVibesOpen');

export const preSessionVibesTutorialCompletedAtom = createDerivedWritableAtom(
  visitorAtom,
  'preSessionVibesTutorialCompleted',
);

export const hasCompletedClientMeasuresAtom = createDerivedWritableAtom(visitorAtom, 'hasCompletedClientMeasures');

export const isRestartingSessionAtom = createDerivedWritableAtom(visitorAtom, 'isRestartingSession');

export const sessionConversationSidAtom = createDerivedWritableAtom(visitorAtom, 'sessionConversationSid');
